import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Shield, Ban, CheckCircle2, Trash2, X } from "lucide-react";
import api from "@/lib/api";
import { ROLE_LABELS } from "@/lib/AuthContext";

const ROLES = ["utente", "socio", "collaboratore", "direttivo", "amministrazione"];
const STATUSES = [
  { v: "active", label: "Attivo" },
  { v: "pending_verification", label: "In attesa email" },
  { v: "suspended", label: "Sospeso" },
];

export default function UsersAdmin() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setItems(data);
    } catch {
      toast.error("Errore caricamento utenti");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveRole = async () => {
    try {
      await api.put(`/admin/users/${editing.id}/role`, {
        role: editing.role,
        membership_note: editing.membership_note || "",
      });
      toast.success("Ruolo aggiornato");
      setEditing(null); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };
  const saveStatus = async (u, status) => {
    try {
      await api.put(`/admin/users/${u.id}/status`, { status });
      toast.success("Stato aggiornato");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const del = async (u) => {
    if (!confirm(`Eliminare definitivamente ${u.email}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Eliminato"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const filtered = items.filter((u) => {
    const matchQ = !q || u.email.toLowerCase().includes(q.toLowerCase()) || (u.name || "").toLowerCase().includes(q.toLowerCase());
    const matchR = roleFilter === "all" || u.role === roleFilter;
    return matchQ && matchR;
  });

  const input = "w-full rounded-xl bg-white border border-[#292522]/12 px-3 py-2 focus:border-[#FF6100] focus:ring-2 focus:ring-[#FF6100]/20 outline-none text-sm";

  return (
    <div data-testid="admin-users">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl font-medium">Utenti</h1>
          <p className="text-sm text-[#292522]/60 mt-1">Gestione ruoli e stato degli account registrati.</p>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#292522]/40" />
          <input placeholder="Cerca per email o nome…" value={q} onChange={(e) => setQ(e.target.value)} className={input + " pl-9"} data-testid="users-search" />
        </div>
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className={input + " max-w-[220px]"} data-testid="users-role-filter">
          <option value="all">Tutti i ruoli</option>
          {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-sm text-[#292522]/60">Caricamento…</div>
      ) : (
        <div className="overflow-x-auto card-vv bg-white">
          <table className="w-full text-sm">
            <thead className="bg-[#F8F1E8] text-[#292522]/70 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Utente</th>
                <th className="text-left px-4 py-3">Ruolo</th>
                <th className="text-left px-4 py-3">Stato</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Iscritto</th>
                <th className="text-right px-4 py-3">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id} className="border-t border-[#292522]/6" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{u.name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || "—"}</div>
                    <div className="text-xs text-[#292522]/60">{u.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#FFF1E8] text-[#FF6100] px-2.5 py-0.5 text-xs font-semibold">
                      <Shield className="h-3 w-3" /> {ROLE_LABELS[u.role] || u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={u.status} />
                  </td>
                  <td className="px-4 py-3 text-xs text-[#292522]/60 hidden md:table-cell">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString("it-IT") : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => setEditing({ ...u, membership_note: "" })} className="p-1.5 hover:text-[#FF6100]" title="Cambia ruolo" data-testid={`user-role-${u.email}`}>
                        <Shield className="h-4 w-4" />
                      </button>
                      {u.status !== "active" ? (
                        <button onClick={() => saveStatus(u, "active")} className="p-1.5 hover:text-[#216B65]" title="Attiva">
                          <CheckCircle2 className="h-4 w-4" />
                        </button>
                      ) : (
                        <button onClick={() => saveStatus(u, "suspended")} className="p-1.5 hover:text-[#B12A2A]" title="Sospendi">
                          <Ban className="h-4 w-4" />
                        </button>
                      )}
                      <button onClick={() => del(u)} className="p-1.5 hover:text-red-600" title="Elimina">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-[#292522]/50">Nessun utente</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-[#292522]/50 flex items-center justify-center p-4" data-testid="role-modal">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-[#292522]/8">
              <h2 className="font-display text-2xl font-medium">Cambia ruolo</h2>
              <button onClick={() => setEditing(null)}><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="text-xs text-[#292522]/60">Utente</div>
                <div className="font-semibold">{editing.email}</div>
              </div>
              <label className="block">
                <span className="text-xs font-semibold text-[#292522]/70">Nuovo ruolo</span>
                <select value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} className={input + " mt-1"} data-testid="role-select">
                  {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-[#292522]/70">Nota (opzionale, verrà inclusa nell'email)</span>
                <textarea rows="3" value={editing.membership_note || ""} onChange={(e) => setEditing({ ...editing, membership_note: e.target.value })} className={input + " mt-1"} placeholder="Es. Ti confermiamo l'iscrizione a socio 2026…" />
              </label>
            </div>
            <div className="p-5 border-t flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="btn-ghost text-sm">Annulla</button>
              <button onClick={saveRole} className="btn-primary text-sm" data-testid="role-save">Salva</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    active: { label: "Attivo", cls: "bg-[#DDEEE8] text-[#216B65]" },
    pending_verification: { label: "In attesa email", cls: "bg-[#F5E9C7] text-[#7A6820]" },
    suspended: { label: "Sospeso", cls: "bg-[#FBE3E3] text-[#B12A2A]" },
  }[status || "active"];
  return <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${cfg?.cls || ""}`}>{cfg?.label || status}</span>;
}
